import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../features/auth/service";
import { parseApiError } from "../lib/http/apiErrors";

const ForgotPasswordPage: React.FC = () => {
  const navigate                  = useNavigate();
  const [email,     setEmail]     = useState("");
  const [sent,      setSent]      = useState(false);
  const [error,     setError]     = useState("");
  const [isLoading, setLoading]   = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await authService.forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(parseApiError(err, "Something went wrong. Try again.").message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-shell flex min-h-screen items-center justify-center bg-gradient-to-br from-surface-subtle via-surface to-brand-muted/30 px-4 py-10">
      <div className="w-full max-w-sm animate-fade-in-up sm:max-w-md">

        <div className="mb-8 text-center">
          <img src="/gullivr_new.png" alt="Gullivr" className="mx-auto mb-4 h-12 w-auto object-contain" />
          <h1 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl">Forgot password?</h1>
          <p className="mt-2 text-sm text-ink-muted">
            {sent
              ? "Check your inbox for a reset link."
              : "Enter your email and we'll send you a reset link."}
          </p>
        </div>

        <div className="glass-panel p-6 sm:p-8">
          {error && <div className="alert-error mb-6">{error}</div>}

          {sent ? (
            <div className="space-y-5 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-ink-muted">
                We sent a reset link to <span className="font-semibold text-ink">{email}</span>.
                It expires in <span className="font-semibold text-ink">15 minutes</span>.
              </p>
              <p className="text-xs text-ink-muted">
                Didn't get it? Check spam or{" "}
                <button
                  type="button"
                  onClick={() => { setSent(false); setError(""); }}
                  className="font-semibold text-brand hover:text-brand-hover"
                >
                  try again
                </button>.
              </p>
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="btn btn-lg w-full bg-black text-white hover:bg-gray-900 active:scale-[0.98]"
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="label" htmlFor="fp-email">Email address</label>
                <input
                  id="fp-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="input input-lg w-full"
                  placeholder="you@company.com"
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-lg w-full bg-black text-white hover:bg-gray-900 active:scale-[0.98] disabled:opacity-50"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="spinner-sm border-white/30 border-t-white" aria-hidden="true" />
                    Sending…
                  </span>
                ) : "Send reset link"}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="text-sm font-medium text-ink-muted hover:text-ink"
                >
                  ← Back to sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
